/* eslint-disable no-shadow */
/*
 *
 * Settlements reducer
 *
 */
import produce from 'immer';
import moment from 'moment';

import {
  DEFAULT_ACTION,
  UPDATE_TABLE_DATA,
  HEADINGS,
  SAVE_SETTLEMENTS,
  SAVE_IMPORT_SETTLEMENTS,
  ENABLE_LOADER,
  DISABLE_LOADER,
  UPDATE_FILTERS,
  RESET_FILTERS,
  RESET_PAGE,
  RESET_DATA,
  UPDATE_SETTLEMENT_HOLIDAYS,
  UPDATE_UNSETTLED_AMOUNT,
  SAVE_LIVE_SETTLEMENT,
  FETCH_LIVE_SETTLEMENT,
  SET_SETTLEMENT_DETAILS_DATA,
  RESET_SETTLEMENT_DETAILS_DATA,
  SET_SETTLEMENT_TYPE_DATA,
  SAVE_NEXT_SETTLEMENTS_DETAILS,
} from './constants';

import {
  DEFAULT_PG_MENU_TAB_STATE,
  DEFAULT_CURRENT_PAGE,
  STANDARD,
  INSTANT,
  VENDOR,
  ON_DEMAND,
} from '../../utils/constants';

const {
  filters: { appliedFilters, dateFilters, ...f },
  ...rest
} = DEFAULT_PG_MENU_TAB_STATE;

export const getInitialState = () => ({
  isLoading: false,
  tabs: {
    [STANDARD]: DEFAULT_PG_MENU_TAB_STATE,
    [INSTANT]: DEFAULT_PG_MENU_TAB_STATE,
    [ON_DEMAND]: DEFAULT_PG_MENU_TAB_STATE,
    [VENDOR]: {
      ...rest,
      filters: {
        ...f,
        appliedFilters: {},
      },
    },
  },
  headingData: HEADINGS.STANDARD,
  holidaysData: {},
  unSettledAmountData: {
    unSettledAmount: null,
    lastUpdatedAt: '',
  },
  eligibleFundsData: {
    eligibleFunds: null,
    lastUpdatedAt: '',
  },
  settlementsTemplateType: '',
  settlementDetailsData: {},
  settlementTabType: 'online',
  nextSettlementsDetails: {},
});

export const initialState = {
  ...getInitialState(),
};

/* eslint-disable default-case, no-param-reassign */
const settlementsReducer = (state = initialState, action) =>
  produce(state, draft => {
    switch (action.type) {
      case SAVE_NEXT_SETTLEMENTS_DETAILS: {
        draft.nextSettlementsDetails = action.data || {};
        break;
      }
      case SAVE_SETTLEMENTS: {
        const { data, activeNavigation } = action.payload;
        // set the data as per activeNavigation
        if (activeNavigation === STANDARD) {
          const response = data || {};
          const {
            merchantPayments = {},
            merchantPaymentDashboardConfiguration = {},
          } = response;

          draft.tabs[activeNavigation].data = merchantPayments.content || [];
          draft.tabs[activeNavigation].pagination = {
            totalElements: merchantPayments.totalElements || 0,
            first:
              typeof merchantPayments.first === 'boolean'
                ? merchantPayments.first
                : true,
            last:
              typeof merchantPayments.last === 'boolean'
                ? merchantPayments.last
                : true,
          };
          draft.settlementsTemplateType =
            merchantPaymentDashboardConfiguration.templateType || '';
        } else if (activeNavigation === ON_DEMAND)
          draft.tabs[activeNavigation].data = data.content;
        else draft.tabs[activeNavigation].data = data;

        draft.isLoading = false;
        break;
      }
      case SAVE_IMPORT_SETTLEMENTS: {
        const { data, activeNavigation } = action.payload;
        draft.tabs[activeNavigation] = {
          ...(draft.tabs[activeNavigation] || {}),
          data,
        };
        draft.isLoading = false;
        break;
      }
      case UPDATE_TABLE_DATA: {
        const {
          totalCount,
          currentPageRowData,
          hasPrevEnabled,
          hasNextEnabled,
          activeNavigation,
        } = action.data;
        draft.tabs[activeNavigation].totalCount = totalCount;
        draft.tabs[activeNavigation].hasPrev = hasPrevEnabled;
        draft.tabs[activeNavigation].hasNext = hasNextEnabled;
        draft.tabs[activeNavigation].currentPageRowData = currentPageRowData;
        break;
      }
      case ENABLE_LOADER: {
        draft.isLoading = true;
        break;
      }
      case DISABLE_LOADER: {
        draft.isLoading = false;
        break;
      }
      case UPDATE_FILTERS: {
        const { data } = action;
        const {
          activeNavigation,
          appliedFilters,
          dateFilters,
          filtersForDisplay,
          ...filters
        } = data;
        if (appliedFilters) {
          if (activeNavigation === VENDOR) {
            const { startDate, endDate, ...rest } = appliedFilters;
            draft.tabs[activeNavigation].filters.appliedFilters = rest;
          } else {
            draft.tabs[
              activeNavigation
            ].filters.appliedFilters = appliedFilters;
          }
        }
        if (dateFilters)
          draft.tabs[activeNavigation].filters.dateFilters = dateFilters;
        draft.tabs[activeNavigation].filters = {
          ...draft.tabs[activeNavigation].filters,
          ...filters,
        };
        if (filtersForDisplay) {
          draft.tabs[activeNavigation].filtersForDisplay = filtersForDisplay;
        }
        break;
      }
      case RESET_FILTERS: {
        const activeNavigation = action.data;
        const { dateFilters } = draft.tabs[activeNavigation].filters;

        if (dateFilters) {
          const { range } = dateFilters;
          draft.tabs[activeNavigation].filters.appliedFilters = {
            ...initialState.tabs[activeNavigation].filters.appliedFilters,
            startDate: moment(range[0]).format('YYYY-MM-DD 00:00:00'),
            endDate: moment(range[1])
              .endOf('day')
              .format('YYYY-MM-DD HH:mm:ss'),
          };
        } else {
          draft.tabs[activeNavigation].filters.appliedFilters = {
            ...initialState.tabs[activeNavigation].filters.appliedFilters,
          };
        }
        draft.tabs[activeNavigation].filtersForDisplay =
          initialState.tabs[activeNavigation].filtersForDisplay;
        break;
      }
      case UPDATE_SETTLEMENT_HOLIDAYS: {
        draft.holidaysData = action.data || {};
        break;
      }
      case RESET_PAGE: {
        const activeNavigation = action.data;
        draft.tabs[activeNavigation].filters.pageNo = DEFAULT_CURRENT_PAGE;
        break;
      }
      case RESET_DATA: {
        const obj = getInitialState();
        Object.keys(obj).forEach(k => (draft[k] = obj[k])); // eslint-disable-line
        break;
      }
      case RESET_SETTLEMENT_DETAILS_DATA: {
        draft.settlementDetailsData = {}; // eslint-disable-line
        break;
      }
      case UPDATE_UNSETTLED_AMOUNT: {
        const { unsettledAmount } = action.data;
        draft.unSettledAmountData.unSettledAmount =
          unsettledAmount > 0 ? unsettledAmount : 0;
        draft.unSettledAmountData.lastUpdatedAt = moment().format('hh:mm A');
        break;
      }
      case SET_SETTLEMENT_DETAILS_DATA: {
        draft.settlementDetailsData = action.data || {};
        break;
      }
      case SET_SETTLEMENT_TYPE_DATA: {
        const { type } = action.data;
        draft.settlementTabType = type;
        break;
      }
      case DEFAULT_ACTION:
        break;
    }
  });

export const liveSettlementReducer = (state, { type, data }) =>
  produce(state, draft => {
    switch (type) {
      case SAVE_LIVE_SETTLEMENT:
        draft.settlementList = data;
        draft.loading = false;
        break;
      case FETCH_LIVE_SETTLEMENT:
        draft.settlementList = {};
        draft.loading = true;
        break;
      default:
        break;
    }
  });

export default settlementsReducer;
